import React, { memo, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PlusIcon from "../../icons/PlusIcon";
import CardArrows from "./CardArrows";

const VHCard = memo(
  ({
    data,
    isActive,
    index,
    onCardSelect,
    handlePrevCard,
    handleNextCard,
    hideCoverImage = false,
    overviewDeck = false,
  }) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.dir() === "rtl";
    const cardName = overviewDeck
      ? t(`heroCards.${data.id}.name`)
      : data.name;
    const cardTitle = overviewDeck
      ? t(`heroCards.${data.id}.title`)
      : data.title;
    const cardDescription = overviewDeck
      ? t(`heroCards.${data.id}.description`)
      : data.description;
    const ctaText = useMemo(() => {
      if (!data.cta) return null;
      return overviewDeck
        ? t(`heroCards.${data.id}.cta`)
        : data.cta.text;
    }, [data.cta, data.id, overviewDeck, t]);

    const handlePlusClick = useCallback(() => {
      if (index !== -1) {
        onCardSelect(index);
      }
    }, [index, onCardSelect]);

    const stopCardNavigate = useCallback((e) => {
      e.stopPropagation();
    }, []);

    const ctaBtnClass = hideCoverImage
      ? "card__btn btn-n btn-n--black btn-n--small vh-card__cta-btn"
      : "card__btn btn-n btn-n--white btn-n--small vh-card__cta-btn";
    const ctaLinkClass = hideCoverImage
      ? "card__btn-link link-n vh-card__cta-link"
      : "card__btn-link link-n link-n--white vh-card__cta-link";

    return (
      <div
        dir={i18n.dir()}
        onClick={handlePlusClick}
        className={`card vh-card ${isActive ? "active" : ""}${
          hideCoverImage ? " vh-card--no-cover" : ""
        }`}
        data-id={data.id}
      >
        {!hideCoverImage && data.image?.src ? (
          <>
            <img
              fetchpriority="high"
              decoding="async"
              width="2704"
              height="1600"
              src={data.image.src}
              className="card__image img-fluid"
              alt={data.image.alt || ""}
              srcSet={data.image.srcSet}
              sizes={data.image.sizes}
            />
            <div className="card__mask"></div>
          </>
        ) : null}

        {!overviewDeck ? (
          <div
            tabIndex={0}
            data-id={data.id}
            className="card__icon-wrapper vh-card__icon-hit"
            role="button"
            aria-label={
              isActive
                ? t("hero.collapseCard", { name: cardName })
                : t("hero.expandCard", { name: cardName })
            }
            onClick={(e) => {
              e.stopPropagation();
              handlePlusClick();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                handlePlusClick();
              }
            }}
          >
            <PlusIcon />
          </div>
        ) : null}
        <div className="card__name">{cardName}</div>

        <div
          className="card__text"
          style={hideCoverImage ? undefined : { paddingBottom: "30px" }}
        >
          <h2
            className={`title-h1 vh-card__title${
              hideCoverImage ? "" : " pl-mid"
            }`}
          >
            {cardTitle}
          </h2>
          <div className={`card__block${hideCoverImage ? "" : " pr-big"}`}>
            <div className="description-n vh-card__description">
              {cardDescription}
            </div>
            {data.cta && ctaText && (
              <div className="vh-card__cta" onClick={stopCardNavigate}>
                <Link
                  to={data.cta.href}
                  className={ctaBtnClass}
                  tabIndex={isActive ? 0 : -1}
                >
                  <span className="btn-n__text">{ctaText}</span>
                </Link>
                <Link
                  to={data.cta.href}
                  className={ctaLinkClass}
                  tabIndex={isActive ? 0 : -1}
                >
                  <span className="link-n__text">{ctaText}</span>
                </Link>
              </div>
            )}
          </div>

          {!overviewDeck && data.info ? (
            <div
              className={`card__info vh-card__info${
                hideCoverImage ? "" : " pr-big"
              }`}
            >
              <div className="card__progress">
                <div className="card__progress-line"></div>
              </div>
              {data.info.type ? (
                <p className="card__subtitle title-h5 vh-card__tech-label">
                  {data.info.type}
                </p>
              ) : null}

              {data.info.logos && data.info.logos.length > 0 ? (
                <div
                  className="card__logos vh-card__tech"
                  role="list"
                  aria-label={data.info.type || "Technologies"}
                >
                  {data.info.logos.map((logo, logoIndex) => (
                    <div
                      key={`${data.id}-logo-${logoIndex}`}
                      className="vh-card__logo-chip"
                      role="listitem"
                      aria-label={logo.alt}
                    >
                      <img
                        decoding="async"
                        src={logo.src}
                        className="card__logo vh-card__logo-img img-fluid"
                        alt=""
                        width="96"
                        height="40"
                      />
                      <span className="vh-card__logo-name" aria-hidden="true">
                        {logo.name || logo.alt}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              {data.info.links && (
                <div className="card__links">
                  {data.info.links.map((link, linkIndex) => (
                    <div key={linkIndex}>
                      <a
                        href={link.href}
                        className="card__link title-h5"
                        tabIndex={-1}
                      >
                        {link.text}{" "}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
        <CardArrows
          onPrev={handlePrevCard}
          onNext={handleNextCard}
          isRtl={isRtl}
        />
        <div className="card__arrows--mobile">
          {isRtl ? (
            <>
              <svg
                onClick={handleNextCard}
                className="card__arrow card__arrow--next"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="button"
                aria-label={t("hero.nextStory")}
              >
                <rect
                  className="card__arrow-circle"
                  x="0.5"
                  y="0.5"
                  width="39"
                  height="39"
                  rx="19.5"
                  stroke="white"
                />
                <path
                  className="card__arrow-icon"
                  d="M20 28L28 20M20 12L28 20M28 20L12 20"
                  stroke="white"
                />
              </svg>
              <svg
                onClick={handlePrevCard}
                className="card__arrow card__arrow--prev"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="button"
                aria-label={t("hero.prevStory")}
              >
                <rect
                  className="card__arrow-circle"
                  x="0.5"
                  y="0.5"
                  width="39"
                  height="39"
                  rx="19.5"
                  stroke="white"
                />
                <path
                  className="card__arrow-icon"
                  d="M20 12L12 20M20 28L12 20M12 20L28 20"
                  stroke="white"
                />
              </svg>
            </>
          ) : (
            <>
              <svg
                onClick={handlePrevCard}
                className="card__arrow card__arrow--prev"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="button"
                aria-label={t("hero.prevStory")}
              >
                <rect
                  className="card__arrow-circle"
                  x="0.5"
                  y="0.5"
                  width="39"
                  height="39"
                  rx="19.5"
                  stroke="white"
                />
                <path
                  className="card__arrow-icon"
                  d="M20 12L12 20M20 28L12 20M12 20L28 20"
                  stroke="white"
                />
              </svg>
              <svg
                onClick={handleNextCard}
                className="card__arrow card__arrow--next"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                role="button"
                aria-label={t("hero.nextStory")}
              >
                <rect
                  className="card__arrow-circle"
                  x="0.5"
                  y="0.5"
                  width="39"
                  height="39"
                  rx="19.5"
                  stroke="white"
                />
                <path
                  className="card__arrow-icon"
                  d="M20 28L28 20M20 12L28 20M28 20L12 20"
                  stroke="white"
                />
              </svg>
            </>
          )}
        </div>
      </div>
    );
  }
);

export default VHCard;
